/**
 * Merchant session and restaurant context.
 *
 * Unlike the customer app, this one requires a real account: a merchant owns
 * menus, prices, and sales data, and an anonymous session has no way to be
 * recovered on another device. `restaurant_staff` links that account to the
 * restaurants it may operate.
 *
 * The selected restaurant is persisted per device, because an iPad on a counter
 * belongs to one restaurant even when the owner has several.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { RestaurantSummary, Session } from '@yummy/db';
import { fetchMyRestaurants, getSession, onAuthStateChange, signOut } from '@yummy/db';
import { usePersistentState } from '@yummy/ui-rnr';

import { supabase } from '../lib/supabase';

interface MerchantState {
  session: Session | null;
  /** Null until the first session check resolves. */
  ready: boolean;
  restaurants: RestaurantSummary[];
  restaurant: RestaurantSummary | null;
  selectRestaurant: (id: string) => void;
  refreshRestaurants: () => Promise<RestaurantSummary[]>;
  /** Ends the session on this device, and leaves the account's others alone. */
  logout: () => Promise<void>;
  /** Ends every session the account has, this device included. */
  logoutEverywhere: () => Promise<void>;
}

const MerchantContext = createContext<MerchantState | null>(null);

/**
 * Reads the merchant session and the active restaurant.
 *
 * @returns Session, restaurant list, and the active restaurant.
 * @throws Error when called outside {@link MerchantProvider}.
 */
export function useMerchant(): MerchantState {
  const value = useContext(MerchantContext);
  if (!value) throw new Error('useMerchant must be used inside MerchantProvider');
  return value;
}

/**
 * Loads the session, the merchant's restaurants, and tracks the active one.
 *
 * @param props Children.
 */
export function MerchantProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [restaurants, setRestaurants] = useState<RestaurantSummary[]>([]);
  // Which account the restaurant list belongs to. Resolving the session is not
  // the same as being ready: between the two, a signed-in merchant looks like an
  // account with no restaurant, which is the onboarding screen — the flash on
  // every refresh. Keying it to the account also covers signing in as someone
  // else, where the previous list is stale rather than empty.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [selectedId, setSelectedId] = usePersistentState<string | null>(
    'yummy.merchant.restaurant',
    null,
  );

  const load = useCallback(async (): Promise<RestaurantSummary[]> => {
    const list = await fetchMyRestaurants(supabase);
    setRestaurants(list);
    return list;
  }, []);

  // Initial session check.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const current = await getSession(supabase);
        if (cancelled) return;
        setSession(current);
      } catch (cause) {
        // A session check that throws must not leave the app waiting forever.
        //
        // The usual cause is an auth callback that could not be completed: a
        // code from a different browser or storage container, an expired link,
        // a malformed hash. `detectSessionInUrl` reads those from the URL, and
        // when it cannot finish it rejects rather than returning null — and an
        // unhandled rejection here meant the sign-in screen was never reached.
        // The customer sees a spinner and no way forward.
        console.warn('Session check failed:', cause);
      } finally {
        // Whoever we are, we are ready to say so. The gate shows the sign-in
        // screen when there is no session, which is the honest answer.
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the session in step with token refreshes and sign-outs.
  useEffect(
    () =>
      onAuthStateChange(supabase, (next) => {
        setSession(next);
        if (!next) setRestaurants([]);
      }),
    [],
  );

  // Load restaurants whenever the signed-in user changes.
  useEffect(() => {
    if (!session) return;
    const account = session.user.id;
    let cancelled = false;
    void (async () => {
      const list = await fetchMyRestaurants(supabase);
      if (cancelled) return;
      setRestaurants(list);
      setLoadedFor(account);
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const restaurant = useMemo(() => {
    if (restaurants.length === 0) return null;
    return restaurants.find((r) => r.id === selectedId) ?? restaurants[0] ?? null;
  }, [restaurants, selectedId]);

  const value = useMemo<MerchantState>(
    () => ({
      session,
      // Ready once the session is known *and* the list for that account has
      // arrived, so neither the onboarding screen nor the previous account's
      // restaurants flash before the right one does.
      ready: ready && (!session || loadedFor === session.user.id),
      restaurants,
      restaurant,
      selectRestaurant: setSelectedId,
      refreshRestaurants: load,
      logout: async () => {
        await signOut(supabase, 'local');
        setRestaurants([]);
        setSelectedId(null);
      },
      // The same teardown, but the session ends on every device: the button in
      // Settings for a tablet or a phone that has gone missing. This device goes
      // too, which is what the confirmation in front of it says.
      logoutEverywhere: async () => {
        await signOut(supabase, 'global');
        setRestaurants([]);
        setSelectedId(null);
      },
    }),
    [session, ready, loadedFor, restaurants, restaurant, setSelectedId, load],
  );

  return <MerchantContext.Provider value={value}>{children}</MerchantContext.Provider>;
}
